//! Debug System Hook - Haupt-Hook für Debug-Overlay

import type { LogDomain, LogEntry, LogFilter, LogLevel, ScopeFilter } from "../entities";

import { listen } from "@tauri-apps/api/event";
import { useCallback, useEffect, useState } from "react";

import { LogService } from "../services/LogService";
import { debugSystem } from "../utils/debugLog";
import { useLogStore } from "./useLogStore";

export interface DebugSystemState {
  // UI State
  autoScroll: boolean;
  // Dynamic Filter Data
  availableDomains: LogDomain[];

  availableFiles: string[];
  availableFunctions: string[];
  availableModules: string[];
  isMinimized: boolean;

  // Overlay State
  isOpen: boolean;
  scopeFilter?: ScopeFilter;
  searchTerm: string;

  // Filter State
  selectedDomains: LogDomain[];
  selectedFile?: string;
  selectedFunction?: string;
  selectedLevel: LogLevel;
  selectedModule?: string;
  showContext: boolean;
  showTimestamps: boolean;
}

export interface DebugSystemActions {
  clearFilters: () => void;
  clearLogs: () => Promise<void>;
  clearScope: () => void;

  exportLogs: (format: string) => Promise<void>;
  maximizeOverlay: () => void;
  minimizeOverlay: () => void;
  // Actions
  refreshLogs: () => Promise<void>;

  selectFile: (file: string) => void;
  selectFunction: (functionName: string) => void;
  // Dynamic Filter Navigation
  selectModule: (module: string) => void;
  setLevel: (level: LogLevel) => void;
  setSearchTerm: (term: string) => void;

  // UI Settings
  toggleAutoScroll: () => void;
  toggleContext: () => void;
  // Filter Control
  toggleDomain: (domain: LogDomain) => void;

  // Overlay Control
  toggleOverlay: () => void;
  toggleTimestamps: () => void;
  updateDynamicFilters: () => void;
}

/**
 * WHAT:
 *   Hook that manages debug system state and actions for the debug overlay.
 *
 * WHY:
 *   Provides unified interface for managing debug overlay, filters, and log operations.
 *
 * BEHAVIOR:
 *   - Manages overlay state (open, minimized)
 *   - Handles filter state (domains, level, search, scope)
 *   - Updates dynamic filters based on available logs
 *   - Sets up event listeners for backend logs
 *   - Provides actions for overlay control, filtering, and log operations
 *
 * RISKS:
 *   - NOTE: Event listener setup in useEffect - may cause memory leaks if not cleaned up
 *   - NOTE: Auto-refresh interval may cause performance issues with large log volumes
 *   - NOTE: Domain normalization logic is complex - may have edge cases
 *
 * @returns Combined state and actions for debug system
 *
 */
export const useDebugSystem = (): DebugSystemState & DebugSystemActions => {
  const logStore = useLogStore();

  // Get overlay state and actions from logStore
  const {
    autoScroll,
    isOverlayMinimized,
    isOverlayOpen,
    setFilter: setStoreFilter,
    setOverlayMinimized,
    setScopeFilter: setStoreScopeFilter,
    toggleAutoScroll,
    toggleOverlay,
  } = logStore;
  const [selectedDomains, setSelectedDomains] = useState<LogDomain[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<LogLevel>("debug");
  const [searchTerm, setSearchTerm] = useState("");
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter | undefined>();
  // autoScroll wird jetzt aus useLogStore geholt
  const [showTimestamps, setShowTimestamps] = useState(true);
  const [showContext, setShowContext] = useState(false);

  // Dynamic Filter State
  const [availableDomains, setAvailableDomains] = useState<LogDomain[]>([]);
  const [availableModules, setAvailableModules] = useState<string[]>([]);
  const [availableFiles, setAvailableFiles] = useState<string[]>([]);
  const [availableFunctions, setAvailableFunctions] = useState<string[]>([]);
  const [selectedModule, setSelectedModule] = useState<string>();
  const [selectedFile, setSelectedFile] = useState<string>();
  const [selectedFunction, setSelectedFunction] = useState<string>();

  // Helper to normalize domain from log entry
  const normalizeDomain = useCallback((domain: LogEntry["domain"]): string => {
    if (typeof domain === "string") {
      return domain;
    }
    if (typeof domain === "object") {
      const domainObj = domain as null | {
        domain?: string;
        label?: string;
        name?: string;
      };
      if (domainObj) {
        return domainObj.name ?? domainObj.domain ?? domainObj.label ?? "unknown";
      }
    }
    return "unknown";
  }, []);

  // Helper to extract unique domains from logs
  const extractDomains = useCallback(
    (logs: LogEntry[]): LogDomain[] => {
      const domains = new Set<string>();
      for (const log of logs) {
        const normalized = normalizeDomain(log.domain);
        domains.add(normalized);
      }
      return [...domains] as LogDomain[];
    },
    [normalizeDomain],
  );

  // Helper to filter logs by selected domains
  const filterLogsByDomains = useCallback(
    (logs: LogEntry[], domains: LogDomain[]): LogEntry[] => {
      if (domains.length === 0) {
        return [];
      }
      return logs.filter((log) => {
        const normalized = normalizeDomain(log.domain);
        return domains.includes(normalized as LogDomain);
      });
    },
    [normalizeDomain],
  );

  // Helper to extract unique modules from filtered logs
  const extractModules = useCallback((logs: LogEntry[]): string[] => {
    const modules = new Set<string>();
    for (const log of logs) {
      if (log.scope.module) {
        modules.add(log.scope.module);
      }
    }
    return [...modules];
  }, []);

  // Helper to extract unique files from filtered logs
  const extractFiles = useCallback((logs: LogEntry[]): string[] => {
    const files = new Set<string>();
    for (const log of logs) {
      if (log.scope.file) {
        files.add(log.scope.file);
      }
    }
    return [...files];
  }, []);

  // Helper to extract unique functions from filtered logs
  const extractFunctions = useCallback((logs: LogEntry[]): string[] => {
    const functions = new Set<string>();
    for (const log of logs) {
      if (log.scope.function) {
        functions.add(log.scope.function);
      }
    }
    return [...functions];
  }, []);

  // Update dynamic filters based on available logs
  const updateDynamicFilters = useCallback(() => {
    const logs = logStore.logs;

    // Extract unique domains from logs
    const domains = extractDomains(logs);
    setAvailableDomains(domains);

    // Update modules based on selected domains
    if (selectedDomains.length > 0) {
      const filteredLogs = filterLogsByDomains(logs, selectedDomains);
      const modules = extractModules(filteredLogs);
      setAvailableModules(modules);
    } else {
      setAvailableModules([]);
    }

    // Update files based on selected module
    if (selectedDomains.length > 0 && selectedModule) {
      const filteredLogs = filterLogsByDomains(logs, selectedDomains);
      const moduleFilteredLogs = filteredLogs.filter((log) => log.scope.module === selectedModule);
      const files = extractFiles(moduleFilteredLogs);
      setAvailableFiles(files);
    } else {
      setAvailableFiles([]);
    }

    // Update functions based on selected file
    if (selectedDomains.length > 0 && selectedModule && selectedFile) {
      const filteredLogs = filterLogsByDomains(logs, selectedDomains);
      const fileFilteredLogs = filteredLogs.filter(
        (log) => log.scope.module === selectedModule && log.scope.file === selectedFile,
      );
      const functions = extractFunctions(fileFilteredLogs);
      setAvailableFunctions(functions);
    } else {
      setAvailableFunctions([]);
    }
  }, [
    extractDomains,
    extractFiles,
    extractFunctions,
    extractModules,
    filterLogsByDomains,
    logStore.logs,
    selectedDomains,
    selectedFile,
    selectedModule,
  ]);

  // Update dynamic filters when logs change
  useEffect(() => {
    updateDynamicFilters();
  }, [updateDynamicFilters]);

  // Clear dependent selections when parent changes
  useEffect(() => {
    if (selectedDomains.length === 0) {
      setSelectedModule(undefined);
      setSelectedFile(undefined);
      setSelectedFunction(undefined);
    }
  }, [selectedDomains]);

  useEffect(() => {
    if (!selectedModule) {
      setSelectedFile(undefined);
      setSelectedFunction(undefined);
    }
  }, [selectedModule]);

  useEffect(() => {
    if (!selectedFile) {
      setSelectedFunction(undefined);
    }
  }, [selectedFile]);

  // Update scope filter when scope selection changes
  useEffect(() => {
    if (selectedDomains.length > 0) {
      if (selectedFunction && selectedFile && selectedModule) {
        setScopeFilter({
          domains: selectedDomains,
          file: selectedFile,
          function: selectedFunction,
          level: "function",
          module: selectedModule,
        });
      } else if (selectedFile && selectedModule) {
        setScopeFilter({
          domains: selectedDomains,
          file: selectedFile,
          level: "file",
          module: selectedModule,
        });
      } else if (selectedModule) {
        setScopeFilter({
          domains: selectedDomains,
          level: "module",
          module: selectedModule,
        });
      } else {
        setScopeFilter({
          domains: selectedDomains,
          level: "domain",
        });
      }
    } else {
      setScopeFilter(undefined);
    }
  }, [selectedDomains, selectedModule, selectedFile, selectedFunction]);

  // Update log store filter when local state changes
  useEffect(() => {
    const filter: LogFilter = {
      domains: selectedDomains,
      limit: 1000,
      min_level: selectedLevel,
    };
    if (searchTerm) {
      filter.message = searchTerm;
    }

    // IMPORTANT: use stable function references from the store, not das ganze Store-Objekt,
    // um zu vermeiden, dass dieser Effekt bei jedem Store-Update erneut getriggert wird.
    setStoreFilter(filter);
    setStoreScopeFilter(scopeFilter);
  }, [
    selectedLevel,
    selectedDomains,
    searchTerm,
    scopeFilter,
    setStoreFilter,
    setStoreScopeFilter,
  ]);

  // Auto-Refresh für Backend-Logs
  useEffect(() => {
    if (isOverlayOpen) {
      const interval = setInterval(() => {
        void (async () => {
          try {
            // Backend-Logs abrufen (alle Logs, nicht gefiltert)
            const response = await LogService.getLogs({
              filter: {
                domains: [], // Alle Domains
                min_level: "debug", // Alle Levels
              },
              limit: 1000,
            });

            if (response.logs.length > 0) {
              // Backend-Logs zu Frontend-Logs hinzufügen
              for (const log of response.logs) {
                logStore.addLog(log);
              }
            }
          } catch {
            // Fehler ignorieren (Zero-Logging Prinzip)
          }
        })();
      }, 1000); // Alle 1 Sekunde

      return () => {
        clearInterval(interval);
      };
    }
  }, [isOverlayOpen, selectedDomains, selectedLevel, searchTerm, scopeFilter, logStore]);

  // Event Listener für Backend-Logs
  useEffect(() => {
    interface LogEventPayload {
      data: LogEntry;
      type?: string;
    }
    interface LogEvent {
      payload: LogEventPayload;
    }
    const unlistenPromise = listen("log-event", (event: LogEvent) => {
      if (event.payload.type === "LogAdded") {
        const logEntry = event.payload.data;
        logStore.addLog(logEntry);
      }
    });

    return () => {
      void unlistenPromise.then((unlistenFunction) => {
        unlistenFunction();
      });
    };
  }, [logStore]);

  // Overlay Control
  const minimizeOverlay = useCallback(() => {
    setOverlayMinimized(true);
  }, [setOverlayMinimized]);

  const maximizeOverlay = useCallback(() => {
    setOverlayMinimized(false);
  }, [setOverlayMinimized]);

  // Filter Control
  const toggleDomain = useCallback((domain: LogDomain) => {
    setSelectedDomains((previous) =>
      previous.includes(domain) ? previous.filter((d) => d !== domain) : [...previous, domain],
    );
  }, []);

  const setLevel = useCallback((level: LogLevel) => {
    setSelectedLevel(level);
  }, []);

  const setSearchTermCallback = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedDomains([]);
    setSelectedLevel("debug");
    setSearchTerm("");
    setScopeFilter(undefined);
    setSelectedModule(undefined);
    setSelectedFile(undefined);
    setSelectedFunction(undefined);
  }, []);

  // Scope Navigation
  const selectModule = useCallback((module: string) => {
    setSelectedModule(module);
    setSelectedFile(undefined);
    setSelectedFunction(undefined);
  }, []);

  const selectFile = useCallback((file: string) => {
    setSelectedFile(file);
    setSelectedFunction(undefined);
  }, []);

  const selectFunction = useCallback((functionName: string) => {
    setSelectedFunction(functionName);
  }, []);

  const clearScope = useCallback(() => {
    setSelectedModule(undefined);
    setSelectedFile(undefined);
    setSelectedFunction(undefined);
  }, []);

  // UI Settings - toggleAutoScroll kommt jetzt aus useLogStore

  const toggleTimestamps = useCallback(() => {
    setShowTimestamps((previous) => !previous);
  }, []);

  const toggleContext = useCallback(() => {
    setShowContext((previous) => !previous);
  }, []);

  // Actions
  const refreshLogs = useCallback(async () => {
    await logStore.refreshLogs();
  }, [logStore]);

  const clearLogs = useCallback(async () => {
    await LogService.clearLogs(); // Kein Parameter = alle Logs löschen
    logStore.clearLogs();
  }, [logStore]);

  const exportLogs = useCallback(
    async (format: string) => {
      try {
        await LogService.exportLogs(format, logStore.currentFilter, "logs.json");
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        debugSystem("error", "Export failed", {
          error: message,
          file: "useDebugSystem.ts",
          function: "exportLogs",
        });
      }
    },
    [logStore],
  );

  const result: DebugSystemState & DebugSystemActions = {
    autoScroll,
    availableDomains,
    availableFiles,
    availableFunctions,
    availableModules,
    clearFilters,
    clearLogs,
    clearScope,
    exportLogs,
    isMinimized: isOverlayMinimized,
    // State
    isOpen: isOverlayOpen,
    maximizeOverlay,
    minimizeOverlay,
    refreshLogs,
    searchTerm,

    selectedDomains,
    selectedLevel,
    selectFile,
    selectFunction,
    selectModule,
    setLevel,
    setSearchTerm: setSearchTermCallback,
    showContext,
    showTimestamps,
    toggleAutoScroll,
    toggleContext,
    toggleDomain,
    // Actions
    toggleOverlay,
    toggleTimestamps,
    updateDynamicFilters,
  };
  if (scopeFilter !== undefined) {
    result.scopeFilter = scopeFilter;
  }
  if (selectedFile !== undefined) {
    result.selectedFile = selectedFile;
  }
  if (selectedFunction !== undefined) {
    result.selectedFunction = selectedFunction;
  }
  if (selectedModule !== undefined) {
    result.selectedModule = selectedModule;
  }
  return result;
};
